import React from 'react';
import { StudyPlanTask } from '../../types/study-plan';

interface WeeklyCalendarViewProps {
  tasks: StudyPlanTask[];
}

const WeeklyCalendarView: React.FC<WeeklyCalendarViewProps> = ({ tasks }) => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const timeSlots = [
    { id: 'morning', label: 'Morning', start: '07:00', end: '09:00' },
    { id: 'late-morning', label: 'Late Morning', start: '09:00', end: '11:00' },
    { id: 'pre-noon', label: 'Pre Noon', start: '11:00', end: '13:00' },
    { id: 'afternoon', label: 'Afternoon', start: '13:00', end: '15:00' },
    { id: 'late-afternoon', label: 'Late Afternoon', start: '15:00', end: '17:00' },
    { id: 'evening', label: 'Evening', start: '17:00', end: '19:00' },
    { id: 'night', label: 'Night', start: '19:00', end: '21:00' }
  ];

  const getTasksForDayAndSlot = (dayIndex: number, slotId: string) => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + dayIndex);
    
    return tasks.filter(task => {
      const taskDate = new Date(task.date);
      return taskDate.toDateString() === targetDate.toDateString() && 
             task.timeSlot === slotId;
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'study': return 'fa-book';
      case 'practice': return 'fa-pen';
      case 'revision': return 'fa-redo';
      default: return 'fa-tasks';
    }
  };

  return (
    <div className="weekly-calendar-view">
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-primary">
            <tr>
              <th style={{ width: '120px' }}>Time Slot</th>
              {days.map(day => (
                <th key={day} className="text-center">{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map(slot => (
              <tr key={slot.id}>
                <td className="table-light">
                  <div className="fw-bold text-primary">{slot.label}</div>
                  <small className="text-muted">{slot.start} - {slot.end}</small>
                </td>
                {days.map((day, dayIndex) => {
                  const dayTasks = getTasksForDayAndSlot(dayIndex, slot.id);
                  return (
                    <td key={`${day}-${slot.id}`} className="p-2" style={{ minHeight: '100px', verticalAlign: 'top' }}>
                      {dayTasks.map(task => (
                        <div
                          key={task.id}
                          className={`card mb-2 border-${getPriorityColor(task.priority)} bg-${getPriorityColor(task.priority)} bg-opacity-10`}
                          style={{ fontSize: '12px' }}
                        >
                          <div className="card-body p-2">
                            <div className={`d-flex align-items-center mb-1 text-${getPriorityColor(task.priority)}`}>
                              <i className={`fas ${getTypeIcon(task.type)} me-1`}></i>
                              <strong>{task.subject}</strong>
                            </div>
                            <div className="text-dark small">
                              {task.chapter}
                            </div>
                            <div className="text-muted" style={{ fontSize: '10px' }}>
                              {task.topic} • {task.duration}min
                            </div>
                          </div>
                        </div>
                      ))}
                      {dayTasks.length === 0 && (
                        <div className="text-muted text-center py-3" style={{ fontSize: '12px' }}>
                          <i className="fas fa-moon"></i>
                          <br />Free Time
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Legend */}
      <div className="mt-4">
        <h6 className="fw-bold text-primary mb-3">
          <i className="fas fa-info-circle me-2"></i>
          Legend
        </h6>
        <div className="row g-3">
          <div className="col-md-3">
            <div className="d-flex align-items-center">
              <div className="badge bg-danger me-2">High</div>
              <small>Priority tasks</small>
            </div>
          </div>
          <div className="col-md-3">
            <div className="d-flex align-items-center">
              <div className="badge bg-warning me-2">Medium</div>
              <small>Regular study</small>
            </div>
          </div>
          <div className="col-md-3">
            <div className="d-flex align-items-center">
              <div className="badge bg-info me-2">Low</div>
              <small>Optional practice</small>
            </div>
          </div>
          <div className="col-md-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-book text-primary me-2"></i>
              <i className="fas fa-pen text-warning me-2"></i>
              <i className="fas fa-redo text-success me-2"></i>
              <small>Study, Practice, Revision</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyCalendarView;